import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Stack,
  TextField
} from "@mui/material";
import React, {ChangeEvent, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {logError} from "../../shared/services/logger.service";
import {enqueueSnackbar} from "notistack";
import {createEstimate, getEstimate, updateEstimate} from "../estimate/estimate.service";
import {Loader} from "@googlemaps/js-api-loader";
import {formatAddressLine, getLocationFromAddress} from "../../shared/misc/location-helpers";
import {EstimateWithComponentsDto} from "../../shared/dtos/estimate-with-components.dto";
import {getClients} from "../client/clients.service";
import {ClientDto} from "../client/dtos/client.dto";
import {AddEstimateDto} from "../../shared/dtos/add-estimate.dto";

export interface EstimateInfoProps {
  onClose: () => void;
  onSave: (estimate: EstimateWithComponentsDto) => void;
  estimateId?: number;
}

const MAX_ADDRESS_SUGGESTIONS = 10;

const EstimateInfoComponent: React.FC<EstimateInfoProps> = React.forwardRef((props, ref) => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<string[]>([]);
  const [savedEstimate, setSavedEstimate] = useState<EstimateWithComponentsDto | null>(null);
  const [updatedEstimate, setUpdatedEstimate] = useState<Partial<AddEstimateDto> | EstimateWithComponentsDto>({});
  const [allClients, setAllClients] = useState<ClientDto[]>([]);
  const [clientSuggestions, setClientSuggestions] = useState<string[]>([]);
  const [clientSearchText, setClientSearchText] = useState<string>();
  const [isFinishing, setIsFinishing] = useState<boolean>(false);

  useEffect(() => {
    const googleMapsLoader = new Loader({
      apiKey: "AIzaSyBLw50vYSozTEviDIpu94K1KuwM4X_hDUM",
      version: "beta",
    });
    
    Promise.all([
      googleMapsLoader.importLibrary("places"),
      googleMapsLoader.importLibrary("maps")
    ])
      .then(([places, maps]) => {
        setAutocompleteService(new (places as google.maps.PlacesLibrary).AutocompleteService());
        
        // Create a dummy map for the PlacesService (required by Google Maps API)
        const mapDiv = document.createElement('div');
        const map = new (maps as google.maps.MapsLibrary).Map(mapDiv, {});
        setPlacesService(new (places as google.maps.PlacesLibrary).PlacesService(map));
        if(props.estimateId) {
          getEstimate(navigate, props.estimateId)
            .then(estimate => {
              setSavedEstimate(estimate);
              // The box edits the whole line; save splits it again (SQ-502).
              setUpdatedEstimate({...estimate, address: formatAddressLine(estimate.address, estimate.city, estimate.state, estimate.postalCode)});
            })
            .catch(e => {
              logError('Error getting estimate', {clientId: props.estimateId}, e);
              enqueueSnackbar(`Error getting estimate: ${e}`, {variant: 'error', autoHideDuration: 5000});
            })
            .finally(() => setIsLoading(false));
        } else {
          setIsLoading(false);
        }
      })
      .catch(e => {
        logError('Error importing google places library', {}, e);
        enqueueSnackbar(`Error importing google places library: ${e}`, {variant: 'error', autoHideDuration: 5000});
      });

    getClients(navigate)
      .then(clients => setAllClients(clients))
      .catch(e => {
        logError('Error getting clients for autocomplete', {}, e);
        enqueueSnackbar(`Error getting clients for autocomplete`, {variant: 'error', autoHideDuration: 5000});
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if(!autocompleteService || !placesService || !updatedEstimate?.address) return;

    autocompleteService.getPlacePredictions({
      input: updatedEstimate.address,
      componentRestrictions: {country: 'us'},
      locationBias: "IP_BIAS",
      region: 'US'
    })
      .then(response => {
        // Get detailed place information for each prediction to include ZIP codes
        const predictionPromises = response.predictions.slice(0, MAX_ADDRESS_SUGGESTIONS).map(prediction => 
          new Promise<string>((resolve) => {
            placesService.getDetails({
              placeId: prediction.place_id,
              fields: ['address_components', 'formatted_address']
            }, (place, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                let formattedAddress = place.formatted_address || prediction.description;
                
                if(formattedAddress.slice(-5) === ', USA') {
                  formattedAddress = formattedAddress.slice(0, -5);
                }
                
                resolve(formattedAddress);
              } else {
                // Fallback to original description if place details fail
                resolve(prediction.description);
              }
            });
          })
        );

        Promise.all(predictionPromises)
          .then(formattedAddresses => {
            setAddressSuggestions(formattedAddresses);
          })
          .catch(e => {
            logError('Error formatting address suggestions with ZIP codes', {}, e);
            // Fallback to original predictions
            setAddressSuggestions(response.predictions.map(p => p.description).slice(0, MAX_ADDRESS_SUGGESTIONS));
          });
      })
      .catch(e => {
        logError('Error getting address suggestions', {address: updatedEstimate.address}, e);
        enqueueSnackbar(`Error getting address suggestions: ${e}`, {variant: 'error', autoHideDuration: 5000});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedEstimate?.address, placesService]);

  useEffect(() => {
    const clientSuggestions = allClients
      .map(c => `${c.name} - ${c.email} (${c.id})`)
      .filter(c => c.toLowerCase().includes(clientSearchText?.toLowerCase() ?? ''))
    setClientSuggestions([...clientSuggestions, '']);
  }, [allClients, clientSearchText]);

  const saveAndClose = async () => {
    if(!updatedEstimate?.clientId || !updatedEstimate.address) {
      enqueueSnackbar('Client Id and address are required', {variant: 'error', autoHideDuration: 5000});
      return;
    }

    const location = await getLocationFromAddress(updatedEstimate.address).catch(e => {
      logError('Failed to get location from address', {address: updatedEstimate.address}, e);
      enqueueSnackbar('Failed to get location from address', {variant: 'error', autoHideDuration: 5000});
      return null;
    });
    if(!location) return;

    // The address field keeps the street alone; city, state and the
    // five-digit zip go in their own (SQ-502).
    const {street, ...place} = location;
    const newEstimate = {...updatedEstimate, ...place, address: street, name: updatedEstimate.name ?? `Estimate for ${updatedEstimate.clientId}`};
    setUpdatedEstimate(newEstimate);

    setIsFinishing(true);
    try {
      const returnedEstimate = (savedEstimate as EstimateWithComponentsDto)?.id
        ? await updateEstimate(navigate, (savedEstimate as EstimateWithComponentsDto).id, newEstimate)
        : await createEstimate(navigate, newEstimate as AddEstimateDto);
      props.onSave(returnedEstimate);
      return returnedEstimate;
    }
    catch {
      logError('Failed to modify estimate', {newLead: newEstimate});
      enqueueSnackbar('Failed to modify estimate', {variant: 'error', autoHideDuration: 5000});
    }
    finally {
      setIsFinishing(false);
      props.onClose();
    }
  }

  const handleClientChange = (client: string) => {
    setClientSearchText(client);
    const idStartIndex = client.lastIndexOf('(') + 1;
    const idEndIndex = client.lastIndexOf(')') - 1;
    if(idStartIndex < 0 || idEndIndex < 0) return;

    const id = parseInt(client.substring(idStartIndex, idEndIndex + 1));
    setUpdatedEstimate(estimate => ({...estimate!, clientId: id}));
  };

  const handleAddressChange = (address: string) => {
    setUpdatedEstimate(estimate => ({...estimate!, address: address ?? undefined}));
  };

  const handleNotesChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUpdatedEstimate(estimate => ({...estimate!, notes: event.target.value ?? undefined}));
  };

  const handleSave = async () => {
    saveAndClose();
  }

  return (
    isLoading ? <CircularProgress/> :
      <Card
        sx={{ maxWidth: 600, maxHeight: '90vh', mx: 'auto', my: 4, overflowY: 'auto' }}>
        <CardContent>
          <CardHeader title={savedEstimate?.id ? 'Estimate Details' : 'Add Estimate'}/>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <Autocomplete
                disablePortal
                freeSolo
                filterOptions={o => o}
                options={clientSuggestions}
                value={clientSearchText ?? ''}
                onInputChange={(_, a) => handleClientChange(a)}
                renderInput={(params) => <TextField {...params} label="Client *"/>}
              />
            </FormControl>
            <FormControl fullWidth>
              <Autocomplete
                disablePortal
                freeSolo
                filterOptions={o => o}
                options={addressSuggestions}
                value={updatedEstimate?.address ?? ''}
                onInputChange={(_, a) => handleAddressChange(a)}
                renderInput={(params) => <TextField {...params} label="Address *"/>}
              />
            </FormControl>
            <FormControl fullWidth>
              <TextField
                label="Notes"
                variant="outlined"
                multiline
                inputProps={{autoCapitalize: 'sentences'}}
                rows={4}
                value={updatedEstimate?.notes ?? ''}
                onChange={handleNotesChange}
              />
            </FormControl>
            <Button onClick={handleSave} variant="contained" component="label">
              {isFinishing ? <CircularProgress/> : 'Save'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
  )
});

export default EstimateInfoComponent;